import React, { useMemo, useState } from "react";
import ReactDOMServer from "react-dom/server";
import { useQuery } from "@tanstack/react-query";

import StatementDocument, { STATEMENTS_EMAIL } from "../common/StatementDocument";
import logo from "../../assests/images/logo-dark-h.png";
import { getAllTransferHistory, getUser } from "src/services";
import { getWalletNumber, getWalletTotalBalance, type WalletLike } from "src/helpers/utils";
import { buildSection, extractCurrencyCode, type StatementSection } from "src/helpers/statement";
import { Title, TitleDescription, TitleWrapper, Wrapper } from "../Styles";
import { Button } from "../common/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../common/ui/select";

/**
 * A statement covers at most three months. Anything longer is put together by
 * the operations team from the admin dashboard, which has no cap.
 */
export const MAX_MONTHS = 3;
export const ALL_CURRENCIES = "ALL";

const hasStoredAuth = () => Boolean(localStorage.getItem("access") || localStorage.getItem("refresh"));
const today = () => new Date().toISOString().split("T")[0];

/** Exported for the tests: the boundary is the part worth pinning down. */
export function monthsBetween(start: string, end: string): number {
  const from = new Date(start);
  const to = new Date(end);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return 0;

  // Calendar months rather than days: three months from 15 Jan is 15 Apr, so a
  // 90-day rule would refuse the last few days of a range a user reads as
  // exactly three months.
  const limit = new Date(from);
  limit.setMonth(limit.getMonth() + MAX_MONTHS);
  return to > limit ? MAX_MONTHS + 1 : MAX_MONTHS;
}

export function isWithinLimit(start: string, end: string): boolean {
  if (!start || !end) return false;
  return monthsBetween(start, end) <= MAX_MONTHS;
}

type WalletChoice = { code: string; walletNumber: string; balance: string | number };

/** One wallet per currency is enforced by the wallet service, so a code names a wallet. */
export function walletChoices(wallets: WalletLike[] | undefined): WalletChoice[] {
  const seen = new Set<string>();
  const out: WalletChoice[] = [];
  for (const w of wallets ?? []) {
    const code = extractCurrencyCode(w.currency_code || w.currency || w.currency_value || w.name);
    const walletNumber = getWalletNumber(w);
    if (!code || !walletNumber || seen.has(code)) continue;
    seen.add(code);
    out.push({ code, walletNumber, balance: getWalletTotalBalance(w).replace(/,/g, "") });
  }
  return out.sort((a, b) => a.code.localeCompare(b.code));
}

const Statements = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currency, setCurrency] = useState<string>(ALL_CURRENCIES);
  const [error, setError] = useState("");
  const [isBuilding, setIsBuilding] = useState(false);

  const { data: userData } = useQuery({
    queryKey: ["userDetails"],
    queryFn: () => getUser(),
    enabled: hasStoredAuth(),
  });

  const choices = useMemo(() => walletChoices(userData?.wallets), [userData?.wallets]);

  const bothDatesChosen = Boolean(startDate && endDate);
  const rangeTooLong = bothDatesChosen && !isWithinLimit(startDate, endDate);

  const mailtoLink = useMemo(() => {
    const subject = "Statement request (over 3 months)";
    const name = [userData?.firstname, userData?.lastname].filter(Boolean).join(" ");
    const body = [
      "Hello Hanypay,",
      "",
      "Please send me a statement for the period below.",
      "",
      `Name: ${name || "-"}`,
      `Email: ${userData?.email || "-"}`,
      `Hanypay ID: ${userData?.hanypay_id || "-"}`,
      `Currency: ${currency === ALL_CURRENCIES ? "All currencies" : currency}`,
      `From: ${startDate || "-"}`,
      `To: ${endDate || "-"}`,
      "",
      "Thank you.",
    ].join("\n");
    return `mailto:${STATEMENTS_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }, [userData, startDate, endDate, currency]);

  const handleGenerate = async () => {
    setError("");
    if (!bothDatesChosen) {
      setError("Choose both a start and an end date.");
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      setError("The start date must come before the end date.");
      return;
    }
    if (rangeTooLong) return;

    const wanted = currency === ALL_CURRENCIES ? choices : choices.filter((c) => c.code === currency);
    if (wanted.length === 0) {
      setError("No wallet found for that currency.");
      return;
    }

    setIsBuilding(true);
    try {
      // Fetched from the start date up to today, not the end date: the
      // opening balance is derived from the balance now, so every posted
      // movement since the period began has to be known.
      const sections: StatementSection[] = await Promise.all(
        wanted.map(async (choice) => {
          const history = await getAllTransferHistory(String(userData?.id), {
            start_date: startDate,
            end_date: today(),
            currency_code: choice.code,
          });
          return buildSection({
            currencyCode: choice.code,
            walletNumber: choice.walletNumber,
            currentBalance: choice.balance,
            transactions: history?.results ?? [],
            startDate,
            endDate,
          });
        }),
      );

      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        setError("Your browser blocked the statement window. Allow pop-ups and try again.");
        return;
      }
      // about:blank cannot resolve a relative asset path, so the logo is
      // handed over as an absolute URL.
      const logoSrc = new URL(logo, window.location.href).toString();
      const label = currency === ALL_CURRENCIES ? "All currencies" : currency;
      printWindow.document.write(`<!doctype html><html><head><meta charset="utf-8" />
<title>Hanypay Statement ${label} ${startDate} to ${endDate}</title>
<style>@page { margin: 12mm; } body { margin: 0; font-family: Arial, sans-serif; } tr { page-break-inside: avoid; }</style>
</head><body>${ReactDOMServer.renderToStaticMarkup(
        <StatementDocument
          userData={userData}
          sections={sections}
          startDate={startDate}
          endDate={endDate}
          logoSrc={logoSrc}
        />,
      )}</body></html>`);
      printWindow.document.close();
      setTimeout(() => printWindow.print(), 300);
    } catch {
      setError("We could not build your statement. Please try again.");
    } finally {
      setIsBuilding(false);
    }
  };

  return (
    <Wrapper>
      <TitleWrapper>
        <Title>Statements</Title>
        <TitleDescription>
          Generate a statement for one wallet, or all of them, for any period up to {MAX_MONTHS} months.
        </TitleDescription>
      </TitleWrapper>

      <div className="mt-6 flex w-full max-w-xl flex-col space-y-4">
        <div className="flex w-full flex-col space-y-2">
          <label>Currency</label>
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger>
              <SelectValue placeholder="Select currency" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_CURRENCIES}>All currencies</SelectItem>
              {choices.map((c) => (
                <SelectItem key={c.code} value={c.code}>
                  {c.code} · {c.walletNumber}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex w-full flex-col space-y-2">
          <label htmlFor="statement-from">From</label>
          <input
            id="statement-from"
            type="date"
            value={startDate}
            max={endDate || today()}
            onChange={(e) => {
              setError("");
              setStartDate(e.target.value);
            }}
            className="border border-slate-200 p-2 outline-red-500"
          />
        </div>

        <div className="flex w-full flex-col space-y-2">
          <label htmlFor="statement-to">To</label>
          <input
            id="statement-to"
            type="date"
            value={endDate}
            min={startDate || undefined}
            max={today()}
            onChange={(e) => {
              setError("");
              setEndDate(e.target.value);
            }}
            className="border border-slate-200 p-2 outline-red-500"
          />
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        {rangeTooLong && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm font-semibold text-amber-900">That period is longer than {MAX_MONTHS} months.</p>
            <p className="mt-1 text-sm text-amber-800">
              Statements covering more than {MAX_MONTHS} months are prepared by our team. Email {STATEMENTS_EMAIL}{" "}
              with the dates you need and we will send it to you.
            </p>
            <a
              href={mailtoLink}
              className="mt-3 inline-flex items-center justify-center rounded-md bg-red-500 px-6 py-2 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-red-600"
            >
              Email the request
            </a>
          </div>
        )}

        <div className="flex justify-end">
          <Button
            type="button"
            onClick={handleGenerate}
            disabled={!bothDatesChosen || rangeTooLong || isBuilding || choices.length === 0}
            className="flex items-center justify-center rounded-md bg-red-500 px-6 py-2 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-red-600 disabled:opacity-50"
          >
            {isBuilding ? "Preparing…" : "Generate statement"}
          </Button>
        </div>
      </div>
    </Wrapper>
  );
};

export default Statements;
